import React, { useState, useEffect } from "react";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import CheckoutProductGrouping from "@src/components/widgets/CheckoutProductGrouping";
import { useNavigate, useLocation } from "react-router-dom";
import OrderSummaryTile, {
  OrderSummaryTileViews,
} from "@src/components/widgets/OrderSummaryTile";
import SpinningLoader from "@src/components/common/SpinningLoader";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import { useCartStore } from "@src/state/cartState";
import * as OrderApi from "@src/api/OrderApi";
import { distributorSlugToName } from "@src/constants/Distributors";
import { extractServerErrorMessage } from "@src/utils/error";

interface Address {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
}

interface DistributorProductGrouping {
  distributorId: string;
  [key: string]: any;
}

interface Cart {
  deliverToAddress?: Address;
  distributorProductGroupings?: DistributorProductGrouping[];
  [key: string]: any;
}

function CheckoutPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, loading: isCartLoading, fetchCart } = useCartStore();
  const [isPlacingOrder, setIsPlacingOrder] = useState<boolean>(false);
  const { deliverToAddress } = cart || {};

  const searchParams = new URLSearchParams(location.search);
  const distributorFilter = searchParams.get("distributor");

  useEffect(() => {
    fetchCart();
  }, []);

  const handlePlaceOrderForDistributor = async (distributorSlug: string) => {
    setIsPlacingOrder(true);
    try {
      const response = await OrderApi.placeOrderForDistributor(distributorSlug);
      if (response.status === 201) {
        showToast({
          message: `Order placed for ${distributorSlugToName(distributorSlug)}`,
          variant: "success",
        });
        const { orderNumber } = response.body;
        if (orderNumber) {
          navigate(`/orders/${orderNumber}/`);
        }
        // Refresh cart so that distributor's items disappear
        await fetchCart();
      }
    } catch (e) {
      console.error(e);
      const err = e as { response?: { body?: unknown } };
      const serverMessage = extractServerErrorMessage(err?.response?.body);
      showToast({
        message: serverMessage || "Error placing order",
        variant: "error",
      });
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <>
      {(isCartLoading || isPlacingOrder) && <SpinningLoader />}
      {!!cart && (
        <div className="spaced-content-v-xl">
          <div>
            <SectionHeader
              header={
                <span className="text-xl" data-testid="checkout-page-header">
                  Checkout
                </span>
              }
            />

            <div className="flex-row spaced-content-lg">
              <div
                className="flex-3 spaced-content-v-lg overflow-x-scroll"
                data-testid="checkout-page-product-list-container"
              >
                {/* Note: Commented out for Beta due to missing functionality */}
                {/* <div className="flex-row padding-bottom-sm">
                                  <div className="flex-1">
                                      <div className="text-lg font-weight-bold">Delivering to:</div>
                                      <div className="margin-y-sm text-lg">
                                          <div>Happy Paws Pet Clinic</div>
                                          {hasAddressSet && (
                                              <AddressDisplay {...deliverToAddress} />
                                          )}
                                      </div>
                                      <Link
                                          className="text-xs"
                                          onClick={() => setShowChangeAddressModal(true)}
                                      >
                                          {hasAddressSet ? "Update" : "Set"} address
                                      </Link>
                                  </div>
                                  <div className="flex-1">
                                      <div className="text-lg font-weight-bold">Payment Method</div>
                                      <div className="margin-y-sm text-lg">ending in 1234</div>
                                      <Link
                                          className="text-xs"
                                          to="/settings"
                                      >
                                          Use a different card
                                      </Link>
                                  </div>
                              </div> */}

                {(cart.distributorProductGroupings || [])
                  .filter((group) =>
                    distributorFilter
                      ? group.distributorSlug === distributorFilter
                      : true,
                  )
                  .map((distributorProductGrouping) => (
                    <React.Fragment
                      key={`distributor-product-grouping-${distributorProductGrouping.distributorId}`}
                    >
                      <CheckoutProductGrouping
                        {...distributorProductGrouping}
                        onPlaceOrder={() =>
                          handlePlaceOrderForDistributor(
                            distributorProductGrouping.distributorSlug,
                          )
                        }
                        disabled={isPlacingOrder}
                      />
                    </React.Fragment>
                  ))}
              </div>

              <div
                className="flex-1"
                style={{ minWidth: "300px" }}
                data-testid="checkout-page-order-summary-tile-container"
              >
                {/** Compute total based on backend subtotals/item counts (already use live pricing);
                                  if distributorFilter is set, only that distributor */}
                {(() => {
                  const visibleGroups = (
                    cart?.distributorProductGroupings || []
                  ).filter((group: any) =>
                    distributorFilter
                      ? group.distributorSlug === distributorFilter
                      : true,
                  );

                  const computedSubtotal = visibleGroups.reduce(
                    (acc: number, group: any) => acc + (group.subtotal || 0),
                    0,
                  );

                  const computedItemCount = visibleGroups.reduce(
                    (acc: number, group: any) => acc + (group.itemCount || 0),
                    0,
                  );
                  const cartWithComputedSubtotal = {
                    ...cart,
                    subtotal: computedSubtotal,
                    itemCount: computedItemCount,
                  } as any;
                  return (
                    <OrderSummaryTile
                      cart={cartWithComputedSubtotal}
                      view={OrderSummaryTileViews.Checkout}
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CheckoutPage;
